import { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiCalendarLine,
  RiTimeLine,
  RiTeamLine,
  RiEditLine,
  RiUserAddLine,
  RiCheckLine,
  RiMapPinLine,
} from "@remixicon/react";
import { TrustJetLogoV2, TrustJetBackground } from "@assets/images";
import { apiClient } from "@/lib/api";
import { Skeleton } from "@/components/ui/Skeleton";
import AddPassengerDrawer from "../components/AddPassengerDrawer";
import { formatTimeInAirportTimezone } from "@/utils/airportTimezone";

const ClientTripSheet = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Fetch quote data using token
  const { data: quote, isLoading, error } = useQuery({
    queryKey: ["clientTripSheet", quoteId, token],
    queryFn: () => apiClient.getQuoteByIdWithToken(quoteId, token),
    //queryFn: () => apiClient.getQuoteById(quoteId),
    enabled: !!quoteId && !!token,
    //enabled: !!quoteId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: 2,
    retryDelay: 1000,
  });

  // Derive accepted bid and flights from quote
  const { acceptedBid, flights, passengerCount } = useMemo(() => {
    if (!quote) return { acceptedBid: null, flights: [], passengerCount: 0 };

    let accepted = null;
    for (const bidItem of (quote.bid_items || [])) {
      const bid = bidItem.bid;
      if (!bid?.bid_options) continue;
      const option = bid.bid_options.find((opt) => opt.status === "ACCEPTED");
      if (option) {
        accepted = bid;
        break;
      }
    }
    if (!accepted) return { acceptedBid: null, flights: [], passengerCount: 0 };

    const buildFlight = (dep, arr, date) => ({
      departure_date: date,
      departure_airport: dep || "",
      arrival_airport: arr || "",
    });

    let flightList;
    if (accepted.leg_itinerary?.length > 1) {
      flightList = accepted.leg_itinerary;
    } else if (accepted.return_date) {
      const outbound =
        accepted.leg_itinerary?.[0] ||
        buildFlight(accepted.departure_airport, accepted.arrival_airport, accepted.departure_date);
      const returnLeg = buildFlight(
        outbound.arrival_airport || accepted.arrival_airport,
        outbound.departure_airport || accepted.departure_airport,
        accepted.return_date
      );
      flightList = [outbound, returnLeg];
    } else if (accepted.leg_itinerary?.length === 1) {
      flightList = accepted.leg_itinerary;
    } else {
      flightList = [
        buildFlight(accepted.departure_airport, accepted.arrival_airport, accepted.departure_date),
      ];
    }

    return {
      acceptedBid: accepted,
      flights: flightList,
      passengerCount: accepted.passenger_count || 0,
    };
  }, [quote]);

  // Route summary
  const routeSummary = useMemo(() => {
    if (!flights.length) return "";
    const dep = flights[0]?.departure_airport || "";
    const arr = flights[0]?.arrival_airport || "";
    if (flights.length > 1) return `${dep} → ${arr}`;
    return `${dep} → ${arr}`;
  }, [flights]);

  // Departure date formatted
  const departureDate = useMemo(() => {
    const date = flights[0]?.departure_date;
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [flights]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { day: "numeric", month: "short" });
  };

  const formatTime = (dateStr, airportIcao) => {
    return formatTimeInAirportTimezone(dateStr, airportIcao);
  };

  // Fetch existing quote passengers
  const { data: quotePassengersData, isLoading: isPassengersLoading } = useQuery({
    queryKey: ["quotePassengers", quoteId],
    queryFn: () => apiClient.getQuotePassengers(quoteId),
    enabled: !!quoteId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Passenger state
  const [passengers, setPassengers] = useState([]);
  const [passengersInitialized, setPassengersInitialized] = useState(false);
  const [passengerDrawerOpen, setPassengerDrawerOpen] = useState(false);
  const [editingPassengerIdx, setEditingPassengerIdx] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-populate passengers from quote passengers API
  useEffect(() => {
    if (passengersInitialized || !quotePassengersData) return;
    const list = Array.isArray(quotePassengersData) ? quotePassengersData : (quotePassengersData?.data || []);
    if (list.length > 0) {
      const mapped = list.map((qp) => {
        const rawDob = qp.birth_date || "";
        const dobMatch = rawDob.match(/^(\d{4})-(\d{2})-(\d{2})/);
        const dateOfBirth = dobMatch ? `${dobMatch[2]}/${dobMatch[3]}/${dobMatch[1]}` : rawDob;
        return {
          fullName: qp.passenger_name || "",
          dateOfBirth,
          nationality: qp.nationality || "",
          passportFiles: [],
          visaFiles: [],
          existingPassportUrl: "",
          existingVisaUrl: "",
          clientPassengerId: qp.client_passenger_id || null,
          quotePassengerId: qp.quote_passenger_id || null,
          weight: "",
          weightUnit: "lbs",
          dietaryCatering: "",
          cabinEnvironment: "",
          medicalMobility: "",
          petAccommodations: "",
        };
      });
      setPassengers(mapped);
    }
    setPassengersInitialized(true);
  }, [quotePassengersData, passengersInitialized]);

  const handleAddPassengerFromDrawer = (passengerData) => {
    if (editingPassengerIdx !== null) {
      setPassengers((prev) => {
        const next = [...prev];
        next[editingPassengerIdx] = passengerData;
        return next;
      });
      setEditingPassengerIdx(null);
    } else {
      setPassengers((prev) => {
        const isDuplicate = prev.some(
          (p) => p.fullName === passengerData.fullName && p.dateOfBirth === passengerData.dateOfBirth
        );
        if (isDuplicate) return prev;
        return [...prev, passengerData];
      });
    }
    setPassengerDrawerOpen(false);
  };

  const openEditPassenger = (pIdx) => {
    setEditingPassengerIdx(pIdx);
    setPassengerDrawerOpen(true);
  };

  const removePassenger = async (pIdx) => {
    const passenger = passengers[pIdx];
    const qpId = passenger?.quotePassengerId;
    if (qpId) {
      try {
        await apiClient.deleteQuotePassenger(qpId);
      } catch (err) {
        console.error("Failed to delete quote passenger:", err);
      }
    }
    setPassengers((prev) => prev.filter((_, i) => i !== pIdx));
  };

  // Submit handler
  const renameWithClientId = (clientId, file) => {
    const client = clientId || "unknown";
    return new File([file], `${client}_${file.name}`, { type: file.type });
  };

  const handleSubmit = async () => {
    if (passengers.length === 0 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const clientId = quote?.client?.client_id || quote?.client?.id;

      // Split into existing (already in DB) and new passengers
      const existingPassengers = passengers.filter((p) => p.clientPassengerId);
      const newPassengers = passengers.filter((p) => !p.clientPassengerId);

      // Update existing passengers with any form changes
      if (existingPassengers.length) {
        await Promise.all(
          existingPassengers.map(async (p) => {
            let passport_url = p.existingPassportUrl || null;
            let visa_url = p.existingVisaUrl || null;
            if (p.passportFiles?.length) {
              const uploaded = await apiClient.uploadFile(
                renameWithClientId(clientId, p.passportFiles[0]),
                "passport"
              );
              passport_url = uploaded?.file_path || null;
            }
            if (p.visaFiles?.length) {
              const uploaded = await apiClient.uploadFile(
                renameWithClientId(clientId, p.visaFiles[0]),
                "visa"
              );
              visa_url = uploaded?.file_path || null;
            }
            let dob = null;
            if (p.dateOfBirth) {
              const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
              dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
            }
            return apiClient.updateClientPassenger(p.clientPassengerId, {
              full_name: p.fullName || "",
              date_of_birth: dob,
              nationality: p.nationality || null,
              passport_num: null,
              passport_url,
              visa_url,
              weight: p.weight || null,
              additional_info: {
                dietary_catering: p.dietaryCatering || null,
                cabin_environment: p.cabinEnvironment || null,
                medical_mobility: p.medicalMobility || null,
                pet_accommodations: p.petAccommodations || null,
              },
            });
          })
        );
      }

      // Only create new passengers via batch API
      let createdPassengers = [];
      if (newPassengers.length) {
        const newPayload = await Promise.all(
          newPassengers.map(async (p) => {
            let passport_url = p.existingPassportUrl || null;
            let visa_url = p.existingVisaUrl || null;
            if (p.passportFiles?.length) {
              const uploaded = await apiClient.uploadFile(
                renameWithClientId(clientId, p.passportFiles[0]),
                "passport"
              );
              passport_url = uploaded?.file_path || null;
            }
            if (p.visaFiles?.length) {
              const uploaded = await apiClient.uploadFile(
                renameWithClientId(clientId, p.visaFiles[0]),
                "visa"
              );
              visa_url = uploaded?.file_path || null;
            }
            let dob = null;
            if (p.dateOfBirth) {
              const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
              dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
            }
            return {
              full_name: p.fullName || "",
              date_of_birth: dob,
              nationality: p.nationality || null,
              passport_url,
              visa_url,
              weight: p.weight || null,
              additional_info: {
                dietary_catering: p.dietaryCatering || null,
                cabin_environment: p.cabinEnvironment || null,
                medical_mobility: p.medicalMobility || null,
                pet_accommodations: p.petAccommodations || null,
              },
            };
          })
        );
        const result = await apiClient.createClientPassengersBatch(clientId, newPayload);
        createdPassengers = Array.isArray(result) ? result : (result?.data || []);
      }

      // Build quote passengers payload from both existing and newly created
      const qId = quote?.quote_id || quote?.id || quoteId;
      if (qId) {
        const quotePassengersPayload = [
          ...existingPassengers.filter((p) => !p.quotePassengerId).map((p) => {
            let dob = null;
            if (p.dateOfBirth) {
              const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
              dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
            }
            return {
              passenger_name: p.fullName || '',
              email: null,
              phone: null,
              passport_num: null,
              nationality: p.nationality || null,
              birth_date: dob,
              client_passenger_id: p.clientPassengerId,
            };
          }),
          ...createdPassengers.map((cp, i) => ({
            passenger_name: cp.full_name || newPassengers[i]?.fullName || '',
            email: null,
            phone: null,
            passport_num: null,
            nationality: cp.nationality || null,
            birth_date: cp.date_of_birth || null,
            client_passenger_id: cp.client_passenger_id || null,
          })),
        ];
        if (quotePassengersPayload.length) {
          await apiClient.createQuotePassengersBatch(qId, quotePassengersPayload);
        }
      }

      toast.success("Passenger details submitted successfully!");
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to submit passengers:", err);
      toast.error("Failed to submit passenger details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quote display ID
  const tripId = quote?.quote_number || quoteId?.slice(-6)?.toUpperCase() || "";

  return (
    <div
      className="h-screen w-full flex items-center justify-center"
      style={{
        backgroundImage: `url(${TrustJetBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Centered card container */}
      <div className="w-full max-w-6xl h-full md:h-[92vh] flex md:rounded-[2rem] overflow-hidden">
        {/* Left branding panel */}
        <div className="hidden md:flex w-2/5 shrink-0 flex-col items-start justify-start p-10 pt-14 bg-transparent">
          <img src={TrustJetLogoV2} alt="TrustJet" className="h-5 mb-6" />
          <h1 className="text-4xl font-semibold text-white leading-tight">
            Passenger Details
          </h1>
        </div>

        {/* Right content panel — frosted white container */}
        <div className="flex-1 flex flex-col min-h-0 bg-white/70 backdrop-blur-sm md:rounded-l-[2rem]">
          {/* Mobile logo */}
          <div className="md:hidden flex items-center gap-2 p-4 pb-0">
            <img src={TrustJetLogoV2} alt="TrustJet" className="h-10" />
          </div>
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-5 md:p-8">
          {/* Error state */}
          {!token && (
            <div className="bg-white rounded-2xl p-8 text-center">
              <p className="text-gray-600">Invalid or missing access token.</p>
            </div>
          )}

          {/* API error / no data state */}
          {token && !isLoading && (error || !quote) && !submitted && (
            <div className="bg-white rounded-2xl p-8 text-center">
              <p className="text-gray-900 font-semibold mb-2">Unable to load trip details</p>
              <p className="text-sm text-gray-500">Please check your link and try again.</p>
            </div>
          )}

          {/* Loading state */}
          {token && isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full rounded-full" />
            </div>
          )}

          {/* Main content */}
          {token && !isLoading && quote && !submitted && (
            <div className="space-y-4">
              {/* Trip title */}
              <h2 className="text-xl text-gray-900">
                Trip <span className="text-primary-900 font-bold">{tripId}</span>
              </h2>

              {/* Trip info box */}
              <div className="bg-gray-50/80 rounded-xl p-4 space-y-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Trip Details</p>
                <div className="flex items-start gap-8">
                  <div className="flex items-center gap-2">
                    <RiTeamLine className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Passenger Count</p>
                      <p className="text-sm font-semibold text-gray-900">{passengerCount}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <RiMapPinLine className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">
                        {flights.length > 1 ? "Round Trip" : "One Way"}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">{routeSummary}</p>
                    </div>
                  </div>
                </div>
                {departureDate && (
                  <div className="flex items-center gap-2">
                    <RiCalendarLine className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase">Departure Date</p>
                      <p className="text-sm font-semibold text-gray-900">{departureDate}</p>
                    </div>
                  </div>
                )}

                       {/* Leg itineraries */}
              <div className="space-y-1">
                {flights.map((flight, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-sm text-gray-700 py-2 border-t border-gray-200"
                  >
                    <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1 text-xs font-medium text-gray-700">
                      <RiFlightTakeoffLine className="w-3.5 h-3.5" />
                      {flight.departure_airport}
                    </span>
                    <RiArrowRightLine className="w-3.5 h-3.5 text-gray-400" />
                    <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1 text-xs font-medium text-gray-700">
                      <RiFlightLandLine className="w-3.5 h-3.5" />
                      {flight.arrival_airport}
                    </span>
                    <span className="ml-auto flex items-center gap-3 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <RiCalendarLine className="w-3 h-3" />
                        {formatDate(flight.departure_date)}
                      </span>
                      {flight.departure_date && (
                        <span className="inline-flex items-center gap-1">
                          <RiTimeLine className="w-3 h-3" />
                          {formatTime(flight.departure_date, flight.departure_airport)}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              </div>

              {/* Passenger Cards Grid */}
              {isPassengersLoading ? (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="flex items-center justify-between bg-white rounded-xl px-4 py-3.5 border border-gray-200">
                      <Skeleton className="h-4 w-28 rounded" />
                      <Skeleton className="h-4 w-4 rounded" />
                    </div>
                  ))}
                </div>
              ) : passengers.length > 0 && (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {passengers.map((pax, pIdx) => (
                    <div
                      key={pIdx}
                      className="flex items-center justify-between bg-white rounded-xl px-4 py-3.5 border border-gray-200"
                    >
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {pax.fullName || `Passenger ${pIdx + 1}`}
                      </span>
                      <button
                        onClick={() => openEditPassenger(pIdx)}
                        className="text-gray-400 hover:text-gray-600 transition-colors ml-2 shrink-0"
                      >
                        <RiEditLine className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Success state */}
          {submitted && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <RiCheckLine className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Thank you!</h2>
              <p className="text-sm text-gray-500">
                Passenger details have been submitted successfully.
              </p>
            </div>
          )}
        </div>

        {/* Bottom action buttons */}
        {token && !isLoading && quote && !submitted && (
          <div className="shrink-0 px-5 pb-5 md:px-8 md:pb-8 pt-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setEditingPassengerIdx(null);
                  setPassengerDrawerOpen(true);
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors"
              >
                <RiUserAddLine className="w-4 h-4" />
                Add Passenger
              </button>
              <button
                onClick={handleSubmit}
                disabled={passengers.length === 0 || isSubmitting}
                className="flex-[1.5] flex items-center justify-center gap-2 py-3 bg-primary-900 text-white text-sm font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-800 transition-colors"
              >
                {isSubmitting ? "Submitting..." : "Submit"}
                <RiCheckLine className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* AddPassengerDrawer */}
      <AddPassengerDrawer
        isOpen={passengerDrawerOpen}
        onClose={() => {
          setPassengerDrawerOpen(false);
          setEditingPassengerIdx(null);
        }}
        onAdd={handleAddPassengerFromDrawer}
        onRemove={async () => {
          if (editingPassengerIdx !== null) {
            await removePassenger(editingPassengerIdx);
            setEditingPassengerIdx(null);
            setPassengerDrawerOpen(false);
          }
        }}
        editingPassenger={
          editingPassengerIdx !== null ? passengers[editingPassengerIdx] : null
        }
        clientId={quote?.client?.client_id || quote?.client?.id}
      />
    </div>
  );
};

export default ClientTripSheet;
